<template>
  <section class="lin-td" id="td">
    <header class="lin-section-head">
      <h2>{{ zh ? 'TouchDesigner 系列 2025–26' : 'The TouchDesigner series, 2025–26' }}</h2>
      <p>{{ zh
        ? `${works.length} 件，${span[0]} 到 ${span[1]}，主體是 2025-11-26 起那 54 天幾乎一天一件。這一系列沒有 fork 按鈕，血緣在模組裡：${modules.length} 個帶日期的共用模組，名字裡的六位數就是它做好的那天。下面按模組的生日排：每一列是一個模組，掛著用到它的作品。`
        : `${works.length} works, ${span[0]} to ${span[1]}; most of them from the 54 days after 2025-11-26, nearly one a day. This series has no fork button; the lineage lives in modules: ${modules.length} shared, dated modules, the six digits in a name being the day it was made. Rows below are modules by birthday, each carrying the works built on it.` }}</p>
    </header>

    <ol class="lin-td-rows">
      <li v-for="m in modules" :key="m.name" class="lin-td-row">
        <div class="lin-td-module">
          <span class="lin-td-date">{{ m.date }}</span>
          <strong>{{ m.name }}</strong>
          <em>{{ zh ? `${m.works.length} 件用到` : `${m.works.length} works` }}</em>
        </div>
        <ul class="lin-td-works">
          <li v-for="w in m.works" :key="w.slug"><lineage-card :n="cardOf(w)" :zh="zh" :to="w.public ? '/td/' + w.slug : undefined" :from="from" small /></li>
        </ul>
      </li>
    </ol>

    <div v-if="loose.length" class="lin-td-loose">
      <p class="lin-label">{{ zh ? '沒掛在共用模組上的' : 'Not on a shared module' }}</p>
      <ul class="lin-tile-grid lin-tile-grid-dense">
        <li v-for="w in loose" :key="w.slug"><lineage-card :n="cardOf(w)" :zh="zh" :to="w.public ? '/td/' + w.slug : undefined" :from="from" small /></li>
      </ul>
    </div>

    <div v-if="devs.length" class="lin-td-devs">
      <p class="lin-label">{{ zh ? `模組開發現場（${devs.length}）` : `Where the modules were built (${devs.length})` }}</p>
      <p class="lin-td-devnote">{{ zh ? '這些資料夾不是作品，是模組做出來的地方：Render、Easing、LUT、Launch Pad、Leap Motion……跟 OpenProcessing 那邊的「起始模板」是同一種東西，列名、不開頁。' : 'These folders are not works; they are where the modules were made: render, easing, LUT, launch pad, Leap Motion. The same kind of thing as the starter templates on the OpenProcessing side: listed, not linked.' }}</p>
      <ul class="lin-td-devlist"><li v-for="d in devs" :key="d.folder"><span>{{ d.folder.slice(0, 6) }}</span>{{ d.folder.slice(7) }}<em>{{ d.nodes.toLocaleString() }} {{ zh ? '節點' : 'nodes' }}</em></li></ul>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import catalog from '@/data/td-catalog.json'
import LineageCard from './LineageCard.vue'
const props = defineProps({ zh: Boolean, from: String })
const works = catalog.works
const bySlug = Object.fromEntries(works.map(w => [w.folder, w]))
const span = computed(() => { const d = works.map(w => w.date).filter(Boolean).sort(); return [d[0], d[d.length - 1]] })
// 模組按生日（名字裡的六位數）排；用到它的作品按日期排
const modules = Object.entries(catalog.sharedModules || {}).map(([name, folders]) => {
  const m = name.match(/(\d{2})(\d{2})(\d{2})/)
  return { name, date: m ? `20${m[1]}-${m[2]}-${m[3]}` : '', works: folders.map(f => bySlug[f]).filter(Boolean).sort((a, b) => (a.date || '').localeCompare(b.date || '')) }
}).filter(m => m.works.length >= 2).sort((a, b) => a.date.localeCompare(b.date))
const onModule = new Set(modules.flatMap(m => m.works.map(w => w.slug)))
const loose = works.filter(w => !onModule.has(w.slug)).sort((a, b) => (a.date || '').localeCompare(b.date || ''))
const devs = (catalog.modules || []).slice().sort((a, b) => a.folder.localeCompare(b.folder))
// 套 LineageCard 的資料形狀：沒有影像的（public=false）當「未公開」畫，看得到名字、點不開
const cardOf = w => ({ id: w.slug, t: w.title, d: w.date, img: w.cover || w.poster || null, p: w.public ? 0 : 1 })
</script>

<style>
.lin-td{margin:0 0 72px;padding-top:26px;border-top:1px solid var(--line,rgba(255,255,255,.08));scroll-margin-top:84px}
.lin-td-rows{list-style:none;margin:0;padding:0;display:grid;gap:26px}
.lin-td-row{content-visibility:auto;contain-intrinsic-size:auto 420px;display:grid;grid-template-columns:220px minmax(0,1fr);gap:22px;align-items:start;padding-top:18px;border-top:1px dashed var(--line,rgba(255,255,255,.08))}
.lin-td-module{display:grid;gap:4px;position:sticky;top:90px}
.lin-td-date{font-size:.74rem;letter-spacing:.06em;color:var(--gold,#d9c267)}
.lin-td-module strong{font-weight:500;font-size:.95rem;overflow-wrap:anywhere}
.lin-td-module em{font-style:normal;font-size:.76rem;color:var(--ink-3,#85858a)}
.lin-td-works{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(112px,1fr));gap:16px 12px}
.lin-td-works li{min-width:0}
.lin-td-loose,.lin-td-devs{margin-top:40px}
.lin-td-devnote{font-size:.86rem;line-height:1.7;color:var(--ink-2,#b4b4b0);max-width:76ch;margin:0 0 14px}
.lin-td-devlist{list-style:none;margin:0;padding:0;columns:2;column-gap:32px;font-size:.86rem;line-height:1.9}
.lin-td-devlist span{color:var(--ink-3,#85858a);margin-right:10px;font-variant-numeric:tabular-nums}
.lin-td-devlist em{font-style:normal;color:var(--ink-3,#85858a);margin-left:8px;font-size:.76rem}
@media(max-width:767px){.lin-td-row{grid-template-columns:1fr;gap:12px}.lin-td-module{position:static}.lin-td-devlist{columns:1}}
</style>
